import React, { Dispatch, SetStateAction, useState } from 'react';
import Table from 'Components/Table';
import { Column, Row } from 'react-table';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { RowActionDropdown } from 'Components/Table/RowActionDropdown';
import useGetRuleQuickFilters from 'Hooks/rule-quickfilters';
import { useTranslation } from 'react-i18next';
import styles from './MerchantsOverridesTable.css';
import DeleteMerchantModal from '../DeleteMerchantModal';
import ActivateMerchantModal from '../ActivateMerchantModal';

type MerchantsOverridesTableOptions = {
    disableClickableRow: boolean;
};

type Props = {
    rawData: FlatMerchantOverrideDataItem[];
    templateId: number;
    columnNames: Column<FlatMerchantOverrideDataItem>[];
    hideKebabMenu?: boolean;
    setShowSingleMerchant?: Dispatch<SetStateAction<boolean>>;
    setCurrMerchant?: Dispatch<SetStateAction<string>>;
    isLoading?: boolean;
    options?: Partial<MerchantsOverridesTableOptions>;
};

const MerchantsOverridesTable = ({
    rawData,
    templateId,
    columnNames,
    hideKebabMenu,
    setShowSingleMerchant,
    setCurrMerchant,
    isLoading = false,
    options
}: Props) => {
    const history = useHistory();
    const { path } = useRouteMatch();
    const { t } = useTranslation();
    const [deleteMerchantIsOpen, setDeleteMerchantIsOpen] = useState(false);
    const [activateMerchantIsOpen, setActivateMerchantIsOpen] = useState(false);
    const [merchantId, setMerchantId] = useState('');
    const [merchantEnabled, setMerchantEnabled] = useState(false);
    const data = React.useMemo(() => rawData, [rawData]);
    const columns = React.useMemo<Column<FlatMerchantOverrideDataItem>[]>(() => {
        return hideKebabMenu
            ? [...columnNames]
            : [
                  ...columnNames,
                  {
                      // same accessor as the merchantId column to have easier access to the id, if problems occur
                      // otherwise id can be accessed from cell.row.merchantId insidee of Ceell render func
                      accessor: 'merchantId',
                      id: 'take-action-on-override',
                      disableSortBy: true,
                      enableFixedWidth: true,
                      width: 40,
                      maxWidth: 40,
                      renderType: 'overrideCell',
                      Cell: ({ value: id, row }: { value: string; row: Row }) => {
                          return (
                              <RowActionDropdown
                                  menuId={`${id}-action-dropdown`}
                                  menuActions={
                                      row.original.canBeDeleted
                                          ? [
                                                {
                                                    testId: 'delete-merchant',
                                                    label: t('Delete'),
                                                    handleAction: () => {
                                                        setDeleteMerchantIsOpen(true);
                                                        setMerchantId(id);
                                                    },
                                                    style: { color: 'var(--red000)' }
                                                }
                                            ]
                                          : [
                                                {
                                                    testId: 'reactivate-deactivate-merchant',
                                                    label: row.original.merchantEnabled
                                                        ? t('Deactivate Merchant')
                                                        : t('Reactivate Merchant'),
                                                    handleAction: () => {
                                                        setActivateMerchantIsOpen(true);
                                                        setMerchantEnabled(row.original.merchantEnabled);
                                                        setMerchantId(id);
                                                    },
                                                    style: { color: 'var(--red000)' }
                                                }
                                            ]
                                  }
                              />
                          );
                      }
                  }
              ];
    }, [columnNames, hideKebabMenu, t]);

    const quickFilters = useGetRuleQuickFilters<'merchantEnabled'>({
        quickFilters: ['modifiedFlag', 'merchantEnabled'],
        qfOverrides: [
            {
                id: 'merchantEnabled',
                data: [
                    {
                        id: 'active',
                        text: t('Active'),
                        options: {
                            conditional: 'eq',
                            valueToMatch: true,
                            fieldToMatch: 'merchantEnabled'
                        }
                    },
                    {
                        id: 'inactive',
                        text: t('Inactive'),
                        options: {
                            conditional: 'eq',
                            valueToMatch: false,
                            fieldToMatch: 'merchantEnabled'
                        }
                    }
                ]
            }
        ],
        initialFilters: [
            {
                id: 'merchantEnabled',
                activeId: []
            }
        ]
    });
    return (
        <div className={styles.tableContainer}>
            <Table
                tableName='merchants-listview'
                showMultiSort
                showRichFilter
                pathToIdOnDataObj='merchantId'
                enablePagination
                initialState={{
                    sortBy: [{ id: 'merchantId', desc: false }],
                    globalFilter: {
                        quickFilters: quickFilters.initialQuickFilters
                    }
                }}
                dataTestId='merchant-overrides-table'
                noDataMessage='noMerchants'
                columns={columns}
                data={data}
                showExpandableSearch
                clickableRow={!options?.disableClickableRow}
                onRowInteractCB={(row) => {
                    if (setShowSingleMerchant && setCurrMerchant) {
                        setShowSingleMerchant(true);
                        setCurrMerchant(row?.values?.merchantId);
                    } else {
                        history.push(`${path}/merchant/${row?.values?.merchantId}`);
                    }
                }}
                quickFilters={quickFilters.filters}
                isLoading={isLoading}
            />
            <DeleteMerchantModal
                isOpen={deleteMerchantIsOpen}
                onClose={() => setDeleteMerchantIsOpen(false)}
                merchantId={merchantId}
                templateRepositoryId={templateId}
            />
            <ActivateMerchantModal
                isOpen={activateMerchantIsOpen}
                onClose={() => setActivateMerchantIsOpen(false)}
                merchantId={merchantId}
                templateRepositoryId={templateId}
                merchantEnabled={merchantEnabled}
            />
        </div>
    );
};

export default MerchantsOverridesTable;
